import React from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { isAssignedAttributeGroup } from '../utils/watchlistAttributePresentation';
import { getCountryChipDisplay, renderCountryChipLabel } from '../components/shared/securityDisplayPrimitives';
import { performanceInsetSx, performancePanelSx } from '../styles/performanceMsiStyles';
import {
    APP_STYLE_COMPONENT_COLORS as MSI_COMPONENT_COLORS,
    APP_STYLE_PALETTE as MSI_PALETTE,
    usesAppStyleGrammar,
} from '../styles/msiStyleTokens';
import {
    displayGenericAttributeGroup,
    genericPerformanceCard,
    genericPerformanceSelectionIdentity,
} from './watchlistGroupingValues';

const ELEVATED_CARD_SX = {
    position: 'relative',
    borderRadius: '6px',
    borderTop: '1px solid rgba(226, 232, 240, 0.98)',
    borderLeft: '1px solid rgba(226, 232, 240, 0.98)',
    borderRight: '1px solid rgba(148, 163, 184, 0.56)',
    borderBottom: '3px solid rgba(100, 116, 139, 0.34)',
    boxShadow:
        'inset 0 1px 0 rgba(255, 255, 255, 0.92), 4px 4px 0 rgba(30, 41, 59, 0.44), 4px 4px 0 1px rgba(148, 163, 184, 0.16), 0 12px 20px rgba(0, 0, 0, 0.22)',
};
const NUDGE_SLOT_WIDTH_PX = 66;

const shorten = (value, max = 20) =>
    typeof value === 'string' && value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value;

const selectionIdentity = genericPerformanceSelectionIdentity;

function chipSx({ active, disabled }) {
    return {
        bgcolor: (theme) => (
            usesAppStyleGrammar(theme)
                ? (active ? MSI_PALETTE.blue100 : MSI_PALETTE.surface)
                : (active
                    ? alpha(theme.palette.common.white, 0.18)
                    : alpha(theme.palette.common.black, 0.34))
        ),
        color: (theme) => (usesAppStyleGrammar(theme)
            ? (active ? MSI_PALETTE.ink900 : MSI_PALETTE.ink700)
            : theme.palette.text.primary),
        border: '1px solid',
        borderColor: (theme) => (usesAppStyleGrammar(theme)
            ? (active ? MSI_COMPONENT_COLORS.focusBorder : MSI_PALETTE.lineDark)
            : (active ? 'rgba(241, 245, 249, 0.52)' : 'rgba(148, 163, 184, 0.6)')),
        opacity: disabled ? 0.5 : 1,
        '& .MuiChip-label': {
            fontSize: '11px',
            fontWeight: 500,
            paddingRight: '8px',
        },
        '&:hover': disabled
            ? {}
            : {
                bgcolor: (theme) => (
                    usesAppStyleGrammar(theme)
                        ? (active ? MSI_PALETTE.blue100 : MSI_COMPONENT_COLORS.rowHover)
                        : (active
                            ? alpha(theme.palette.common.white, 0.24)
                            : alpha(theme.palette.common.white, 0.08))
                ),
            },
    };
}

function formatNudgeMeta(nudgeReturn) {
    if (typeof nudgeReturn !== 'number' || !Number.isFinite(nudgeReturn)) {
        return null;
    }
    const pctValue = nudgeReturn * 100;
    const absPct = Math.abs(pctValue);
    const decimals = absPct >= 10 ? 1 : 2;
    if (absPct < 0.005) {
        return {
            text: '• 0.0%',
            color: '#b0bec5',
            signedText: '0.0%',
        };
    }
    if (pctValue > 0) {
        return {
            text: `▲ ${absPct.toFixed(decimals)}%`,
            color: '#66bb6a',
            signedText: `+${absPct.toFixed(decimals)}%`,
        };
    }
    return {
        text: `▼ ${absPct.toFixed(decimals)}%`,
        color: '#ef5350',
        signedText: `-${absPct.toFixed(decimals)}%`,
    };
}

function CategoryFlowRow({ label, chips, nudgeLookbackLabel, indicatorLabel = 'return', nudgeUnavailableText }) {
    if (!chips.length) return null;

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.75,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.2),
                border: '1px solid',
                borderColor: 'rgba(148, 163, 184, 0.45)',
                ...performanceInsetSx,
                minWidth: 0,
                maxWidth: '100%',
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                }}
            >
                {label}:
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, minWidth: 0 }}>
                {chips.map((chip) => (
                    <Tooltip
                        key={chip.key}
                        title={(
                            [
                                chip.tooltip || '',
                                chip.nudgeLoading && !chip.nudgeMeta
                                    ? `${nudgeLookbackLabel} ${indicatorLabel}: loading`
                                    : '',
                                chip.nudgeMeta
                                    ? `${nudgeLookbackLabel} ${indicatorLabel}: ${chip.nudgeMeta.signedText}`
                                    : !chip.nudgeLoading && nudgeUnavailableText ? `${nudgeLookbackLabel} ${indicatorLabel}: unavailable` : '',
                            ]
                                .filter(Boolean)
                                .join(' • ')
                        )}
                        enterDelay={0}
                    >
                        <span>
                            <Chip
                                label={(
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                        <span>{chip.label}</span>
                                        <Box
                                            sx={{
                                                minWidth: `${NUDGE_SLOT_WIDTH_PX}px`,
                                                height: 16,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                            }}
                                        >
                                            {chip.nudgeMeta ? (
                                                <span
                                                    style={{
                                                        color: chip.nudgeMeta.color,
                                                        fontSize: '12px',
                                                        fontWeight: 700,
                                                        fontVariantNumeric: 'tabular-nums',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {chip.nudgeMeta.text}
                                                </span>
                                            ) : chip.nudgeLoading ? (
                                                <CircularProgress
                                                    size={12}
                                                    thickness={6}
                                                    sx={{ color: '#9fb3c7' }}
                                                />
                                            ) : nudgeUnavailableText ? <span>{nudgeUnavailableText}</span> : null}
                                        </Box>
                                    </Box>
                                )}
                                disabled={chip.disabled}
                                onClick={chip.onClick}
                                sx={chipSx({
                                    active: chip.active,
                                    disabled: chip.disabled,
                                })}
                            />
                        </span>
                    </Tooltip>
                ))}
            </Box>
        </Box>
    );
}

export default function AddRemoveViewsPanel({
    title,
    headerControls = null,
    attributesMeta,
    isSelected,
    onToggleSelection,
    nudgeReturnsBySelectionId = null,
    nudgeLookbackLabel = '',
    indicatorLabel = 'return',
    includeUnassigned = false,
    nudgeLoading = false,
    nudgeUnavailableText = '',
    portfolioDisabled,
    portfolioDisabledTooltip,
    showStatus,
    loading,
    error,
    loadingMessage,
    errorMessage,
}) {
    const coreRow = {
        categoryKey: 'core',
        label: 'Core',
        chips: [
            {
                key: 'portfolio',
                label: 'Portfolio',
                tooltip: portfolioDisabled ? portfolioDisabledTooltip : 'Portfolio',
                disabled: portfolioDisabled,
                active: isSelected({ scope: 'portfolio' }),
                onClick: () => onToggleSelection({ scope: 'portfolio' }),
                nudgeMeta: formatNudgeMeta(
                    (nudgeReturnsBySelectionId || {})[selectionIdentity({ scope: 'portfolio' })]
                ),
                nudgeLoading,
            },
        ],
    };

    const detailRows = [];

    attributesMeta.forEach((attr) => {
        const categories = (attr?.values || []).filter((group) => includeUnassigned || isAssignedAttributeGroup(group));
        if (!categories.length) return;

        detailRows.push({
            categoryKey: `attribute:${attr.key}`,
            label: attr.title,
            chips: categories.map((value) => {
                const card = genericPerformanceCard(attr, value);
                const text = displayGenericAttributeGroup(value);
                const countryDisplay = attr.systemKey === 'country' && isAssignedAttributeGroup(value)
                    ? getCountryChipDisplay(text)
                    : null;
                return {
                    key: genericPerformanceSelectionIdentity(card),
                    label: countryDisplay ? renderCountryChipLabel(countryDisplay) : shorten(text),
                    tooltip: countryDisplay ? countryDisplay.tooltip : text,
                    active: isSelected(card),
                    disabled: false,
                    onClick: () => onToggleSelection(card),
                    nudgeMeta: formatNudgeMeta(
                        (nudgeReturnsBySelectionId || {})[selectionIdentity(card)]
                    ),
                    nudgeLoading,
                };
            }),
        });
    });

    const rows = [
        coreRow,
        ...((showStatus && (loading || error)) ? [] : detailRows),
    ];

    return (
        <Paper
            component="section"
            aria-label={title}
            sx={{
                ...ELEVATED_CARD_SX,
                p: 2,
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#000' : theme.palette.background.paper),
                color: 'text.primary',
                ...performancePanelSx,
            }}
        >
            {headerControls && <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                {headerControls}
            </Box>}

            {showStatus && loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {loadingMessage}
                    </Typography>
                </Box>
            ) : showStatus && error ? (
                <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
                    {errorMessage}
                </Alert>
            ) : null}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 0.9 }}>
                {rows.map((row) => (
                    <CategoryFlowRow
                        key={row.categoryKey}
                        label={row.label}
                        chips={row.chips}
                        nudgeLookbackLabel={nudgeLookbackLabel}
                        indicatorLabel={indicatorLabel}
                        nudgeUnavailableText={nudgeUnavailableText}
                    />
                ))}
            </Box>
        </Paper>
    );
}
